import { useState } from 'react';
import { CopyIcon, CopyCheck, Bot, Mail, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Modern, friendly contact page for Bullflow SaaS
 * Three options in glass‑style cards:
 *  1. Chat with our Assistant GPT
 *  2. Email support for personal help
 *  3. Ask an admin in the members‑only Discord
 */
const ContactPage = () => {
  const [isCopied, setIsCopied] = useState(false);

  /* ──────────── Helpers ──────────── */
  const copyEmail = () => {
    navigator.clipboard.writeText('support@bullflow.io');
    setIsCopied(true);
    toast('Email copied to clipboard!');
    setTimeout(() => setIsCopied(false), 2000);
  };

  const openAssistant = () => {
    window.gtag('event', 'Open GPT Assistant', {
      event_category: 'Button',
      event_label: 'Open GPT Assistant',
      value: 1,
    });
    window.open(
      'https://chatgpt.com/g/g-6822aa2346b88191ac671f926f6a8152-official-bullflow-assistant',
      '_blank'
    );
  };

  const openDiscord = () => {
    window.gtag('event', 'Open Discord', {
      event_category: 'Button',
      event_label: 'Open Discord',
      value: 1,
    });
    window.open('https://discord.gg/mZ6JwbsRH7', '_blank');
  };

  /* ──────────── Inline styles ──────────── */
  const outer = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '90vh',
    minWidth: '70%',
    background: '#0d1117',
    color: '#ffffff',
    padding: '40px 5px', // increased top & bottom padding
    boxSizing: 'border-box',
    borderRadius: '12px', // rounded corners
  };

  const container = {
    width: '100%',
    maxWidth: '800px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px',
  };

  const heading = {
    fontSize: '1.75rem',
    fontWeight: 700,
    textAlign: 'center',
    background: 'linear-gradient(90deg, #8593ff, #fce774)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  };

  const subheading = {
    fontSize: '1rem',
    opacity: 0.8,
    textAlign: 'center',
  };

  const cardWrapper = {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const card = {
    background: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '12px',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
  };

  const cardTitle = {
    fontSize: '1.125rem',
    fontWeight: 600,
    margin: '0px 0 6px',
  };

  const cardText = {
    fontSize: '0.875rem',
    opacity: 0.85,
    marginBottom: '16px',
    lineHeight: 1.4,
  };

  const primaryButton = {
    padding: '10px 22px',
    fontSize: '0.95rem',
    fontWeight: 600,
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    background: 'linear-gradient(90deg, #8593ff 0%, #fce774 100%)',
    color: '#0d1117',
  };

  const helperText = {
    fontSize: '0.75rem',
    opacity: 0.65,
    marginTop: '8px',
  };

  const emailRow = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    userSelect: 'text',
    cursor: 'default',
  };

  const copiedText = {
    fontSize: '0.75rem',
    color: 'lightgreen',
    marginTop: '6px',
  };

  /* ──────────── JSX ──────────── */
  return (
    <div style={outer}>
      <div style={container}>
        <h1 style={heading}>We're here to help</h1>
        <p style={subheading}>Choose the option that suits you best</p>

        <div style={cardWrapper}>
          {/* Assistant GPT Card */}
          <div style={card}>
            <Bot size={36} color="#8593ff" />
            <h2 style={cardTitle}>Chat with Bullflow Assistant</h2>
            <p style={cardText}>Get instant answers to common questions about the platform and how to use it.</p>
            <button style={primaryButton} onClick={openAssistant}>Open Assistant GPT</button>
            <span style={helperText}>(Requires a ChatGPT account)</span>
          </div>

          {/* Discord Card */}
          <div style={card}>
            <MessageCircle size={36} color="#5865F2" />
            <h2 style={cardTitle}>Ask Staff in Discord</h2>
            <p style={cardText}>Bullflow members can hop into our private Discord and get help from staff.</p>
            <button style={primaryButton} onClick={openDiscord}>Open Discord</button>
            <span style={helperText}>(Members‑only channel)</span>
          </div>

          {/* Email Support Card */}
          <div style={card}>
            <Mail size={36} color="#fce774" />
            <h2 style={cardTitle}>Email our Support Team</h2>
            <p style={cardText}>Need personalised help? Reach out via email and we’ll get back shortly.</p>
            <div style={emailRow}>
              <span>support@bullflow.io</span>
              {isCopied ? (
                <CopyCheck size={14} color="lightgreen" />
              ) : (
                <CopyIcon size={14} color="#A9A9A9" style={{ cursor: 'pointer' }} onClick={copyEmail} />
              )}
            </div>
            {isCopied && <span style={copiedText}>Copied!</span>}
          </div>


        </div>
      </div>
    </div>
  );
};

export default ContactPage;
